import React, { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode;
  error?: string;
  helpText?: string;
  fullWidth?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      error,
      helpText,
      id,
      disabled = false,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || React.useId();
    
    return (
      <div className={cn('flex flex-col', fullWidth ? 'w-full' : 'w-fit', className)}>
        <div className="flex items-center space-x-3">
          <div className="flex items-center h-5">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              disabled={disabled}
              className={cn(
                'h-4 w-4 rounded',
                'focus:ring-2 focus:ring-offset-0',
                'bg-white border-2',
                'text-orange-500 focus:ring-orange-500',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-all duration-200',
                error ? 'border-red-500' : 'border-orange-300',
                className
              )}
              {...props}
            />
          </div>
          {label && (
            <label
              htmlFor={checkboxId}
              className={cn(
                'text-sm font-medium text-gray-800',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {label}
            </label>
          )}
          {helpText && (
            <div className="group relative">
              <span className="text-xs text-gray-500 hover:text-orange-600 cursor-help">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
              <div className="hidden group-hover:block absolute z-10 w-64 p-2 mt-1 -ml-32 text-xs text-gray-700 bg-white border-2 border-orange-200 rounded-lg shadow-xl">
                {helpText}
              </div>
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
